import { type FC, type PropsWithChildren, useEffect } from 'react'

import { useNativeBridge } from '@dhl/native-bridge'
import { OneTrustProvider, useOneTrustContext, useUpdateLanguageOnChange } from '@dhl/onetrust'

import { useAdobeTagmanagerScript } from './analytics/adobe/useAdobeTagmanagerScript.js'
import { useGtmScript } from './analytics/gtm/useGtmScript.js'
import { useVirtualPageViews } from './analytics/gtm/useVirtualPageViews.js'
import { useDataLayerForProvider } from './analytics/useDataLayerForProvider.js'
import { type DatadogConfig } from './datadog/datadog.js'
import { useDatadog } from './datadog/useDatadog.js'
import { NativeTracking } from './NativeTracking.js'
import { useTrackingState } from './useTrackingState.js'

export type ThirdPartyConfig = {
  oneTrustId?: string
  oneTrustNativeEnabled?: boolean
  oneTrustNativeEnabledFromVersion?: string
  gmtId?: string
  virtualPageRootPath?: string
  nonce: string
  isLoggedIn?: boolean
  adobeTagManagerUrl?: string
  topLevelDomain?: string
}

type Props = {
  config: ThirdPartyConfig
  datadogConfig?: DatadogConfig
  onEnableTracking?: () => void
}

export const ThirdParty: FC<PropsWithChildren<Props>> = ({ children, onEnableTracking, config, datadogConfig }) => {
  const {
    oneTrustId,
    gmtId,
    nonce,
    virtualPageRootPath,
    isLoggedIn = false,
    adobeTagManagerUrl,
    topLevelDomain = 'NL',
  } = config

  const { isTrackingAllowed: isNativeTrackingAllowed, requestNativeTrackingPermission } = useNativeBridge()
  const { isOneTrustLoaded } = useOneTrustContext()

  const { isOneTrustEnabled, isOneTrustEnabledInNative, hasAnalyticsConsent, showTrackingScreen } = useTrackingState(
    config,
    isNativeTrackingAllowed
  )

  useGtmScript(gmtId, nonce, hasAnalyticsConsent)
  useAdobeTagmanagerScript(adobeTagManagerUrl, nonce, hasAnalyticsConsent)
  useVirtualPageViews(!!gmtId && hasAnalyticsConsent, virtualPageRootPath, isLoggedIn)
  useUpdateLanguageOnChange(isOneTrustEnabled)

  useEffect(() => {
    if (showTrackingScreen) {
      requestNativeTrackingPermission()
    }
  }, [requestNativeTrackingPermission, showTrackingScreen])

  // enable internal datalayer to provide data
  useDataLayerForProvider({
    meta: {
      isLoggedIn,
      topLevelDomain,
    },
    providers: {
      isAdobeEnabled: Boolean(adobeTagManagerUrl && isOneTrustEnabled && hasAnalyticsConsent),
    },
  })

  useEffect(() => {
    if (!isOneTrustEnabled) {
      return
    }
    onEnableTracking?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to listen to tracking enable
  }, [isOneTrustEnabled])

  useDatadog(datadogConfig, isOneTrustLoaded)

  return (
    <OneTrustProvider id={oneTrustId} nonce={nonce} shouldInitalize={isOneTrustEnabled}>
      {children}
      {isOneTrustEnabledInNative && <NativeTracking />}
    </OneTrustProvider>
  )
}
